<script lang="ts">
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import { mdiBriefcaseOutline } from "@mdi/js"

  import { resolveCalendarSyncBadge } from "../../_shared/_logic/calendar-sync-badge"
  import { internalOrderTypeLabel } from "../../../../../../../../../../../../_shared/_logic/order-type-labels"
  import type { BoardEvent } from "../../../../../../../../../_logic/calendar-types"

  type BoardPropertyRef = NonNullable<BoardEvent["property"]>

  interface Props {
    item: BoardEvent
    formatAddress?: (property?: BoardPropertyRef) => string
    calendarSyncEnabled?: boolean
  }

  const { item, formatAddress = () => "", calendarSyncEnabled = false }: Props = $props()
  const orderTypeLabel = $derived(internalOrderTypeLabel(item.workOrder) ?? "Auftrag")

  const planningFlag = $derived(item.planningFlag ?? item.event.planning_flag)
  const planningFlagLabel = $derived(
    item.planningFlagLabel ??
      (planningFlag === "TIGHT" ? "Eng" : planningFlag === "OVERFLOW" ? "Überlauf" : ""),
  )
  const planningFlagColor = $derived(
    planningFlag === "OVERFLOW" ? "rose" : planningFlag === "TIGHT" ? "amber" : "blue",
  )
  const showStatus = $derived(
    item?.event?.event_type === "APPOINTMENT" && Boolean(item.statusLabel),
  )
  const calendarSyncBadge = $derived(resolveCalendarSyncBadge(item.event, calendarSyncEnabled))
</script>

<div class="event-title">{item.addressLabel || formatAddress(item.property) || item.title}</div>
{#if item.addressLabel || item.property}
  <div class="event-subtitle">{item.title}</div>
{/if}
{#if calendarSyncBadge}
  <div class="event-sync">
    <StatusTag
      class="calendar-provider-sync-tag"
      size="sm"
      label={calendarSyncBadge.label}
      color={calendarSyncBadge.color}
      icon={calendarSyncBadge.icon}
      tooltipContent={calendarSyncBadge.tooltip}
      tooltipHtml={true}
      disableTitle={true}
    />
  </div>
{/if}

<div class="event-footer">
  {#if showStatus}
    {#if item.statusDefinition}
      <StatusTag size="sm" status={item.statusDefinition} />
    {:else}
      <span class="status-dot" style={`background:${item.color}`}></span>
      <span class="status-text">{item.statusLabel}</span>
    {/if}
  {/if}
  {#if planningFlagLabel}
    <StatusTag size="sm" label={planningFlagLabel} color={planningFlagColor} />
  {/if}
  {#if item.workOrder}
    {#if showStatus || planningFlagLabel}
      <span class="separator">•</span>
    {/if}
    <span class="order">
      <Icon path={mdiBriefcaseOutline} size="0.85rem" />
      {orderTypeLabel}
    </span>
  {/if}
</div>
{#if item.subtitle}
  <div class="event-subtitle">{item.subtitle}</div>
{/if}

<style lang="less">
  .event-sync {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.35rem;
    font-weight: 700;
    font-size: 0.95rem;
  }

  .event-title {
    flex: 0 0 auto;
    font-weight: 800;
    font-size: 0.7rem;
    letter-spacing: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-subtitle {
    flex: 0 0 auto;
    font-size: 0.9rem;
    color: var(--cal-text-muted, #6d97b0);
    line-height: 1.2;
  }

  .event-footer {
    flex: 0 0 auto;
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
    color: var(--cal-text-muted, #6d97b0);
    font-size: 0.85rem;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    display: inline-block;
  }

  .status-text {
    font-weight: 700;
    color: var(--cal-text, #2f4858);
  }

  .separator {
    opacity: 0.6;
  }

  .order {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .event-sync :global(.calendar-provider-sync-tag.status-tag .status-tag__icon) {
    flex: 0 0 1.25rem;
    width: 1.25rem;
    min-width: 1.25rem;
    max-width: 1.25rem;
    height: auto;
    min-height: 0;
    max-height: none;
    box-sizing: border-box;
    display: inline-flex;
    align-self: stretch;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .event-sync :global(.calendar-provider-sync-tag.status-tag .status-tag__icon svg) {
    width: 0.65rem;
    height: 0.65rem;
  }
</style>
